<template>
  <v-container fluid class="pa-5" v-if="showComponent">
    <v-layout class="init-container">
      <div class="init-card" :style="boxStyle">

        <!-- CUANDO QUIERE HACER LA PRIMERA CONEXION -->
        <connect-campaign 
          v-if="!connecting && !campaignTransfer && !transferGap && !autoRetry"
          :loading="loading"
          @start-campaign="startCampaign"
        />

        <!-- MIENTRAS ESTA EN EL REINTENTO -->
        <auto-retry
          v-else-if="autoRetry"
          :retry-count-down="countdown"
          @cancel-auto-retry="resetLoad"
        />

        <!-- MIENTRAS ESTA SIENDO TRANSFERIDO PERO AUN NO TIENE LA INFORMACION -->
        <transfer-gap
          v-else-if="transferGap"
        />

        <!-- MIENTRAS ESTA SIENDO TRANSFERIDO O CONECTADO, Y TIENE LA INFORMACION -->
        <connecting
          v-else-if="connecting"
          :campaign-transfer="campaignTransfer"
          :campaign-name="campaignName"
          :disable-transfer-button="!keepCountDown"
          :countdown="countdown"
          @transfer-now="keepCountDown = false"
        />

      </div>
     </v-layout>
    </v-container>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import EventBus from '@/event-bus';
import permissionsMixin from '@/mixins/permissions';
import AutoRetry from '@/components/CallList/ConnectionStages/AutoRetry.vue';
import Connecting from '@/components/CallList/ConnectionStages/Connecting.vue';
import TransferGap from '@/components/CallList/ConnectionStages/TransferGap.vue';
import ConnectCampaign from '@/components/CallList/ConnectionStages/ConnectCampaign.vue';

export default {
  components: { 
    AutoRetry,
    Connecting,
    TransferGap,
    ConnectCampaign,
  },
  mixins: [
    permissionsMixin,
  ],
  data() {
    return {
      loading:          false,
      countdown:        0,
      autoRetry:        false,
      connecting:       false,
      transferGap:      false,
      campaignName:     '',
      keepCountDown:    false,
      showComponent:    false,
      campaignTransfer: false,
    }
  },
  beforeRouteEnter(to, from, next) {
    // CHANGE_HERE_VOICEMAIL
    Vue.axios.get('check_release', {
      params: {
        name: 'call_list_queues',
      },
    }).then(({ data: { is_active: isActive } }) => {
      if (
        !isActive 
      ) {
        return Vue.router.push({ name: 'home' });
      }
      return next((vm) => {
        vm.showComponent = true;
      });
    });
  },
  mounted() {
    if (!this.canConnectCampaign) {
      return this.$router.push({ name: 'home' });
    }

    let campaignTransfer = this.$store.state.dialerV2.isCampaignTransfer;
    let retryConnection = this.$store.state.dialerV2.retryConnection;

    if (!campaignTransfer && !retryConnection) {
      return;
    }
    
    // Sin llamadas
    if (retryConnection) {
      return this.startCampaign();
    }

    // Transferencia de campaña
    this.transferGap = true;
    this.campaignTransfer = true;
    this.connecting = true;
    return this.playSound()
      .then(() => {
        this.startCampaign();
      });
  },
  beforeRouteLeave (to,from,next) {
    if (!this.$store.state.dialerV2.isCampaignTransfer) {
      this.resetLoad();
      return next();
    }

    EventBus.$emit('modal', 'No puede abandonar esta pestaña mientras este conectado.');
    return next(false);
  },
  methods: {
    playSound() {
      const audio = new Audio(require('@/assets/sounds/transfer_notification.mp3'));
      return audio.play();
    },
    async startCampaign() {
      this.loading = true;
      this.$store.commit('dialerV2/SET_IS_CAMPAIGN_TRANSFER', true);
      this.$store.commit('dialerV2/SET_RETRY_CONNECTION', false);

      const data = await this.getAndProcessConnectAttemp();

      if (data.err) {
        return this.handleErrorAndReset(data.err);
      }

      if (data.message === 'CALL_LIST_NOT_FOUND') {
        this.autoRetry = true;
        return this.startAutoRetry();
      } 

      if (data.message === 'NO_COMPANY_EMAIL') {
        this.resetLoad();
        return EventBus.$emit('modal', `No se encuentra email para la compañía: ${data.company_name}. Por favor, contactarse con su supervisor.`);
      }

      this.campaignName = data.call_list_name;
      this.$store.commit('callTimeAndAlert/SET_CALL_ALERT_CONFIGURATION', data.call_duration);
      
      if (data.message === 'TRANSFER_COMPANY') {
        window.Echo.connector.pusher.config.auth.headers.Authorization = `Bearer ${data.new_token}`;

        await this.$auth.fetch()
        .catch((err) => {
          this.handleErrorAndReset(err);
        });
      }

      this.transferGap = false;

      if (this.campaignTransfer && !this.autoRetry) {
        await this.countDownPromise(30);
      }

      this.autoRetry = false;

      // 'CURRENT_COMPANY' or 'TRANSFER_COMPANY'
      await this.connectDialer(data.call_list_id, data.live_kit_token, data.call_list_name, data.max_commitment_days)
      .catch((err) => (
        this.handleErrorAndReset(err)
      ));
    },
    resetLoad(){
      this.$store.commit('dialerV2/SET_IS_CAMPAIGN_TRANSFER', false);
      this.loading          = false;
      this.autoRetry        = false;
      this.connecting       = false;
      this.transferGap      = false;
      this.campaignName     = '';
      this.keepCountDown    = false;
      this.campaignTransfer = false;
    },
    async connectDialer(callListId, liveKitToken, callListName, maxCommitmentDays) {
      this.connecting   = true;
      this.campaignName = callListName;

      await this.secondsWait(3);
      await this.$store.dispatch('dialerV2/startLiveKit', { callListId, maxCommitmentDays, auth: liveKitToken });
      this.$store.commit('dialerV2/SET_IS_CAMPAIGN_TRANSFER', false);
    },
    handleErrorAndReset(err) {
      this.resetLoad();
      console.log(err);
      EventBus.$emit('modal', `Ocurrio un error. Por favor, intente nuevamente.`);
    },
    secondsWait(seconds) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve('ok');
        }, seconds * 1000);
      });
    },
    async startAutoRetry() {
      let res = await this.countDownPromise(30);

      if (!res) {
        return;
      }

      return this.startCampaign();
    },
    getAndProcessConnectAttemp() {
      return this.axios.get('call_center/get_campaign_to_start')
        .then(({ data }) => data)
        .catch((err) => ({ err }));
    },
    countDownPromise(countdownSeconds) {
      this.countdown = countdownSeconds;
      this.keepCountDown = true;
      let interval;
      let startCountdown = moment.utc();
      return new Promise((resolve) => {
        interval = setInterval(() => {
          if (!this.keepCountDown) {
            clearInterval(interval);
            return resolve(false);
          }

          let diff = moment.utc().diff(startCountdown, 'seconds');
          this.countdown = countdownSeconds - diff;
          if (diff >= countdownSeconds) {
            clearInterval(interval);
            return resolve(true);
          }
        }, 200);
      });
    },
  },
  computed: {
    canConnectCampaign() {
      return this.checkPermissions([
        'connect_campaign_call_center',
      ]);
    },
    boxStyle() {
      if ((this.connecting && !this.campaignTransfer) || this.transferGap) {
        return {
          height: '220px',
          width: '450px',
        };
      }

      if (this.autoRetry) {
        return {
          height: '290px',
          width: '450px',
        };
      }

      if (!this.campaignTransfer) {
        return {
          height: '390px',
          width: '350px',
        };
      }

      return {
        height: '260px',
        width: '450px',
      };
    }
  }
}

</script>

<style scoped>
.init-container { 
  display: flex;
  justify-content: center;
}

.init-card {
  padding: 30px 20px;
  height: 400px;
  width: 350px;
  margin-top: 100px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: height 1.0s, width 1.0s;
}
</style>


// WEBPACK FOOTER //
// src/components/CallList/ConnectCampaign.vue